import { customAlphabet } from "nanoid";
import { IMovementElementItem, TModuleOptions, TTaxResume } from "../models/storageModel";
import dayjs from "dayjs";

export function createId(length: number) {
  const alphabet = "0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZ"
  const nanoid = customAlphabet(alphabet, length)
  let str = nanoid(length)

  if (str.startsWith('-') || str.startsWith('_')) {
    str = 'A' + str.slice(1);
  }
  if (str.endsWith('-') || str.endsWith('_')) {
    str = str.slice(0, -1) + 'z';
  }
  return str
}

export function roundValue(value: number, decimals: number = 2): number {
  const factor = Math.pow(10, decimals);
  return Math.round((value + Number.EPSILON) * factor) / factor;
}

export function addToUniquesArray(array: any[], element: any, id: any, position: 'start' | 'end' = 'end') {
  array = array.filter((item: any) => !JSON.stringify(item).includes(id));
  if (position === 'start') {
    array.unshift(element);
  } else {
    array.push(element);
  }
  return array;
}

export function removeDuplicatesByKey(array: any, key: any) {
  const seenKeys = new Set();
  return array.filter((item: any) => {
    const keyValue = item[key];
    if (seenKeys.has(keyValue)) {
      return false;
    }
    seenKeys.add(keyValue);
    return true;
  });
}

export function getTotalTaxes(itemList: IMovementElementItem[]): TTaxResume[] {
  const taxMap = new Map<string, TTaxResume>();
  itemList.forEach(item => {
    if (item.taxes_list) {
      item.taxes_list.forEach(tax => {
        if (taxMap.has(tax.key)) {
          const existing = taxMap.get(tax.key)!;
          taxMap.set(tax.key, {
            ...existing,
            tax_amount: existing.tax_amount + tax.tax_amount
          });
        } else {
          taxMap.set(tax.key, { ...tax });
        }
      });
    }
  });
  return Array.from(taxMap.values());
}

export function getItemTaxes(item: IMovementElementItem, taxesOptions: TModuleOptions[], type: "SELL_TAX" | "PURCHASE_TAX") {
  return item.module_options
    .filter(el => el.sub_type === type && el.is_active)
    .map(taxModuleOption => {
      const taxConfig = taxesOptions.find(el => el.id === taxModuleOption.option_id);
      if (!taxConfig) return null;

      const tax_amount = taxConfig.type === "TAX_RATE" ?
        (taxConfig.numeric_value || 0) / 100 * ((item.quantity * item.unit_price) * (1 - item.discount_rate / 100)) :
        taxConfig.numeric_value || 0;
      return {
        id: taxConfig.id || 0,
        key: taxConfig.key || "",
        tax_amount: roundValue(tax_amount) || 0,
        description: taxConfig.string_value || ""
      } as TTaxResume;
    })
    .filter((tax): tax is TTaxResume => tax !== null);
}

export function textNumberFormater(text: string | number, format: string = 'money') {
  if (text === 0 || text === "0") return "0";
  if (!text && text !== 0) return "";
  if (format === "money") {
    const num = typeof text === "number" ? text : Number(text);
    if (!Number.isNaN(num)) {
      return num.toLocaleString("es-CO");
    }
    return text.toString();
  }
  return text.toString();
}

export function customNanoId(size: number = 10) {
  const alphabet = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
  const nanoid = customAlphabet(alphabet, 10);
  return nanoid(size)
}

/**
 * Formatea los valores de un input numerico para convertirlo a separadores de mil por puntos y de coma para decimales
 * @param value 
 * @returns 
 */
export function formatInputNumber(value: any): string {
  if (value === null || value === undefined || value === "") return "";
  const stringValue = value.toString();
  const [integerPart, decimalPart] = stringValue.split(".");
  const formattedInteger = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, ".");
  return decimalPart !== undefined ? `${formattedInteger},${decimalPart}` : formattedInteger;
};

/**
 * Formatea los valores de un input numerico para convertirlo al valor estandar de Javascript
 * @param value 
 * @returns 
 */
export function parseInputNumber(value: any): any {
  if (value === null || value === undefined || value === "") return "";
  const stringValue = String(value);
  const cleaned = stringValue
    .replace(/[$%\s]/g, "")
    .replaceAll('.', "")
    .replace(",", ".");
  return cleaned;
};

/**
 * Resize long texts and add ...
 * @param text
 * @param maxLength
 * @returns 
 */
export function resizeText(text: any, maxLength: number) {
  if (text.length > maxLength) {
    return text.slice(0, maxLength) + "...";
  }
  return text;
}

export function getDocumentLabels(type: string | undefined) {
  switch (type) {
    case 'CREDIT_NOTE':
      return { title: 'Nota de Crédito', counterparty: 'Cliente:', user: 'Vendedor:' };
    case 'DEBIT_NOTE':
      return { title: 'Nota de Débito', counterparty: 'Cliente:', user: 'Vendedor:' };
    case 'SALE_INVOICE':
      return { title: 'Comprobante de Venta', counterparty: 'Cliente:', user: 'Vendedor:' };
    case 'PURCHASE_ORDER':
      return { title: 'Orden de Compra', counterparty: 'Proveedor:', user: 'Solicitante:' };
    case 'PURCHASE_RECEIPT':
      return { title: 'Comprobante de Compra', counterparty: 'Proveedor:', user: 'Recibido por:' };
    case 'PURCHASE_REQUEST':
      return { title: 'Solicitud de Compra', counterparty: 'Proveedor:', user: 'Solicitante:' };
    case 'QUOTATION':
      return { title: 'Cotización', counterparty: 'Ofrecido a:', user: 'Elaborado por:' };
    case 'TRANSFER':
      return { title: 'Comprobante de Transferencia', counterparty: 'Responsable:', user: 'Realizado por:' };
    case 'ADJUSTMENT':
      return { title: 'Comprobante de Ajuste', counterparty: 'Responsable:', user: 'Realizado por:' };
    case 'RETURN_IN':
      return { title: 'Devolución en Venta', counterparty: 'Cliente:', user: 'Recibido por:' };
    case 'RETURN_OUT':
      return { title: 'Devolución en Compra', counterparty: 'Proveedor:', user: 'Enviado por:' };
    case 'INITIAL_INVENTORY':
      return { title: 'Inventario Inicial', counterparty: 'Responsable:', user: 'Registrado por:' };
    case 'PARTNER_CONTRIBUTION':
      return { title: 'Aporte de Socio', counterparty: 'Socio:', user: 'Recibido por:' };
    case 'WASTE':
      return { title: 'Baja de Inventario', counterparty: 'Responsable:', user: 'Registrado por:' };
    case 'PRODUCTION_ENTRY':
      return { title: 'Entrada de Producción', counterparty: 'Responsable:', user: 'Realizado por:' };
    case 'PRODUCTION_EXIT':
      return { title: 'Salida de Producción', counterparty: 'Responsable:', user: 'Realizado por:' };
    case 'CONVERSION':
      return { title: 'Fraccionamiento de Inventario', counterparty: 'Responsable:', user: 'Realizado por:' };
    default:
      return { title: 'Comprobante de Movimiento', counterparty: 'Tercero:', user: 'Usuario:' };
  }
}

/**
 * Formats a date/timestamp to a standard UI display string (default 'DD/MM/YYYY hh:mm A')
 */
export function formatDateTime(dateStr?: string | number | Date | null, format: string = 'DD/MM/YYYY hh:mm A'): string {
  if (!dateStr) return '-';
  const d = dayjs(dateStr);
  return d.isValid() ? d.format(format) : '-';
}

/**
 * Formats a date to 'DD/MM/YYYY' for UI display
 */
export function formatDate(dateStr?: string | number | Date | null, format: string = 'DD/MM/YYYY'): string {
  return formatDateTime(dateStr, format);
}

export const PAYMENT_METHOD_LABELS: Record<string, string> = {
  TRANSFER: 'Transferencia Bancaria',
  BANK_TRANSFER: 'Transferencia Bancaria',
  CASH: 'Efectivo',
  CREDIT_CARD: 'Tarjeta de Crédito',
  DEBIT_CARD: 'Tarjeta Débito',
  CARD: 'Tarjeta Débito/Crédito',
  CHECK: 'Cheque',
  CREDIT: 'Crédito',
  NEQUI: 'Nequi',
  DAVIPLATA: 'Daviplata',
  OTHER: 'Otro'
};

/**
 * Returns a human-friendly label for payment methods
 */
export function getPaymentMethodLabel(method: string | undefined, paymentOptions?: TModuleOptions[]): string {
  if (!method) return 'Contado';
  const found = paymentOptions?.find(opt => opt.key === method || opt.legal_code === method);
  if (found?.string_value) return found.string_value;
  return PAYMENT_METHOD_LABELS[String(method).toUpperCase()] || method.replace(/_/g, ' ');
}

export interface TimelineEventInfo {
  label: string;
  color: string;
}

/**
 * Returns a user-friendly label and tag color for movement timeline event types
 */
export function getTimelineEventInfo(eventType: string | undefined, title?: string): TimelineEventInfo {
  const type = String(eventType || '').toUpperCase();
  const lowerTitle = String(title || '').toLowerCase();

  switch (type) {
    case 'CREATED':
      return { label: 'Creación', color: 'blue' };
    case 'ORDER_CREATED':
      return { label: 'Radicado', color: 'blue' };
    case 'DOCUMENT_APPROVED':
      return { label: 'Aprobado', color: 'cyan' };
    case 'STOCK_RECEIVED':
      return { label: 'Recepción en Bodega', color: 'green' };
    case 'FULL_FULFILLMENT': {
      const isExit = lowerTitle.includes('despacho') || lowerTitle.includes('salida') || lowerTitle.includes('entrega');
      return { label: isExit ? 'Despacho Total' : 'Recepción Total', color: 'green' };
    }
    case 'PARTIAL_FULFILLMENT': {
      const isExit = lowerTitle.includes('despacho') || lowerTitle.includes('salida') || lowerTitle.includes('entrega');
      return { label: isExit ? 'Despacho Parcial' : 'Recepción Parcial', color: 'orange' };
    }
    case 'PAYMENT_RECORDED':
      return { label: 'Abono / Pago', color: 'gold' };
    case 'DOCUMENT_SETTLED':
      return { label: 'Liquidado', color: 'purple' };
    case 'CANCELLED':
    case 'VOIDED':
      return { label: 'Anulado', color: 'red' };
    default:
      return { label: type.replace(/_/g, ' ') || 'Evento', color: 'default' };
  }
}

export interface AccountingEntryTypeInfo {
  label: string;
  color: string;
}

/**
 * Returns human-friendly label and tag color for accounting entry types
 */
export function getAccountingEntryTypeInfo(entryType: string | undefined): AccountingEntryTypeInfo {
  const type = String(entryType || '').toUpperCase();
  switch (type) {
    case 'PURCHASES':
      return { label: 'Comprobante de Compra', color: 'purple' };
    case 'SALES':
      return { label: 'Comprobante de Venta', color: 'blue' };
    case 'PAYMENT':
    case 'PAYMENTS':
      return { label: 'Comprobante de Egreso', color: 'green' };
    case 'RECEIPT':
    case 'RECEIPTS':
      return { label: 'Recibo de Caja', color: 'cyan' };
    case 'INVENTORY_ADJUSTMENT':
      return { label: 'Ajuste de Inventario', color: 'orange' };
    case 'PRODUCTION':
      return { label: 'Comprobante de Producción', color: 'magenta' };
    case 'JOURNAL':
      return { label: 'Comprobante de Diario', color: 'default' };
    case 'TRANSFER':
      return { label: 'Traslado de Cuentas', color: 'geekblue' };
    default:
      return { label: type.replace(/_/g, ' ') || 'Comprobante', color: 'default' };
  }
}

/**
 * Replaces raw database enum codes with user-friendly Spanish terms in descriptions and concepts
 */
export function formatHumanDescription(desc: string | undefined): string {
  if (!desc) return '';
  let result = desc;

  result = result
    .replace(/Documento\s+PURCHASE_RECEIPT/gi, 'Comprobante de Compra')
    .replace(/\bPURCHASE_RECEIPT\b/g, 'Comprobante de Compra')
    .replace(/Documento\s+SALE_INVOICE/gi, 'Factura de Venta')
    .replace(/\bSALE_INVOICE\b/g, 'Factura de Venta')
    .replace(/Documento\s+PURCHASE_ORDER/gi, 'Orden de Compra')
    .replace(/\bPURCHASE_ORDER\b/g, 'Orden de Compra')
    .replace(/Documento\s+PURCHASE_REQUEST/gi, 'Solicitud de Compra')
    .replace(/\bPURCHASE_REQUEST\b/g, 'Solicitud de Compra')
    .replace(/Documento\s+QUOTATION/gi, 'Cotización')
    .replace(/\bQUOTATION\b/g, 'Cotización')
    .replace(/Documento\s+CREDIT_NOTE/gi, 'Nota de Crédito')
    .replace(/\bCREDIT_NOTE\b/g, 'Nota de Crédito')
    .replace(/Documento\s+DEBIT_NOTE/gi, 'Nota de Débito')
    .replace(/\bDEBIT_NOTE\b/g, 'Nota de Débito')
    .replace(/Documento\s+TRANSFER/gi, 'Comprobante de Traslado')
    .replace(/Documento\s+ADJUSTMENT/gi, 'Comprobante de Ajuste')
    .replace(/\(TRANSFER\)/gi, '(Transferencia Bancaria)')
    .replace(/\(BANK_TRANSFER\)/gi, '(Transferencia Bancaria)')
    .replace(/\(CASH\)/gi, '(Efectivo)')
    .replace(/\(CREDIT_CARD\)/gi, '(Tarjeta de Crédito)')
    .replace(/\(DEBIT_CARD\)/gi, '(Tarjeta Débito)')
    .replace(/\(CARD\)/gi, '(Tarjeta Débito/Crédito)')
    .replace(/\(NEQUI\)/gi, '(Nequi)')
    .replace(/\(DAVIPLATA\)/gi, '(Daviplata)')
    .replace(/\(CHECK\)/gi, '(Cheque)')
    .replace(/\(CREDIT\)/gi, '(Crédito)')
    .replace(/\(OTHER\)/gi, '(Otro)');

  return result;
}